import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";

const WHATSAPP_AUTH_BASE_URL = `${API_BASE_URL.replace(
  /\/$/,
  "",
)}/api/v1/auth/whatsapp`;

// ✅ Changed from "+265" | "+27" to string so any country code works
export type SupportedPhoneCountryCode = string;

export type WhatsAppAuthIntent =
  | "login"
  | "signup"
  | "register"
  | "phone_update";

export type StartWhatsAppAuthPayload = {
  countryCode: SupportedPhoneCountryCode;
  phoneNumber: string;
  intent?: WhatsAppAuthIntent;
  nameHint?: string;
  firebaseUid?: string;
};

export type VerifyWhatsAppAuthPayload = {
  countryCode: SupportedPhoneCountryCode;
  phoneNumber: string;
  code: string;
};

export type CompleteWhatsAppProfilePayload = {
  firebaseUid: string;
  username: string;
  email?: string;
  password?: string;
};

export type WhatsAppAuthStartResponse = {
  success: boolean;
  message: string;
  challengeId: string;
  expiresAt: string;
  fullPhoneNumber: string;
  existingAccountFound?: boolean;
};

export type WhatsAppAuthVerifyResponse = {
  success: boolean;
  customToken: string;
  firebaseUid: string;
  fullPhoneNumber: string;
  requiresProfileCompletion: boolean;
  user: any;
};

export type CompleteWhatsAppProfileResponse = {
  success?: boolean;
  message?: string;
  user?: any;
};

export const stripToDigits = (value: string) =>
  String(value || "").replace(/[^\d]/g, "");

export const normalizeCountryCode = (
  countryCode: SupportedPhoneCountryCode,
) => {
  const digits = stripToDigits(countryCode);

  // ✅ Keep Malawi as safe fallback
  if (!digits) {
    return "265";
  }

  return digits;
};

export const normalizeLocalPhoneNumber = (phoneNumber: string) => {
  return stripToDigits(phoneNumber).replace(/^0+/, "");
};

export const buildWhatsAppInternationalNumber = ({
  countryCode,
  phoneNumber,
}: {
  countryCode: SupportedPhoneCountryCode;
  phoneNumber: string;
}) => {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const normalizedPhoneNumber = normalizeLocalPhoneNumber(phoneNumber);

  if (!normalizedCountryCode) {
    throw new Error("Please select a valid country code.");
  }

  if (!normalizedPhoneNumber) {
    throw new Error("Please enter your WhatsApp number.");
  }

  // ✅ If user pasted full international number, avoid duplicating country code
  if (normalizedPhoneNumber.startsWith(normalizedCountryCode)) {
    return normalizedPhoneNumber;
  }

  return `${normalizedCountryCode}${normalizedPhoneNumber}`;
};

const getApiErrorMessage = (error: any) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

export const isDuplicatePhoneError = (message: string) => {
  const value = String(message || "").toLowerCase();

  return (
    value.includes("already linked") ||
    value.includes("already used") ||
    value.includes("another account") ||
    value.includes("phone-number-already-exists") ||
    value.includes("phone already exists") ||
    value.includes("phone already in use") ||
    value.includes("duplicate") ||
    value.includes("in use")
  );
};

export const startWhatsAppAuth = async (
  payload: StartWhatsAppAuthPayload,
): Promise<WhatsAppAuthStartResponse> => {
  try {
    const response = await axios.post(`${WHATSAPP_AUTH_BASE_URL}/start`, {
      ...payload,
      countryCode: `+${normalizeCountryCode(payload.countryCode)}`,
      phoneNumber: stripToDigits(payload.phoneNumber),
    });

    return response.data;
  } catch (error: any) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const verifyWhatsAppAuth = async (
  payload: VerifyWhatsAppAuthPayload,
): Promise<WhatsAppAuthVerifyResponse> => {
  try {
    const response = await axios.post(`${WHATSAPP_AUTH_BASE_URL}/verify`, {
      ...payload,
      countryCode: `+${normalizeCountryCode(payload.countryCode)}`,
      phoneNumber: stripToDigits(payload.phoneNumber),
      code: stripToDigits(payload.code),
    });

    return response.data;
  } catch (error: any) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const completeWhatsAppProfile = async (
  payload: CompleteWhatsAppProfilePayload,
): Promise<CompleteWhatsAppProfileResponse> => {
  try {
    const response = await axios.post(
      `${WHATSAPP_AUTH_BASE_URL}/complete-profile`,
      payload,
    );

    return response.data;
  } catch (error: any) {
    throw new Error(getApiErrorMessage(error));
  }
};
